import { Component, Injector, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { FormBase } from '../../../../../../components/common/form-base/form-base';
import { LocalizationModel } from '../../../../models/localization-model';
import { LocalizationNavigationService } from '../../../../services/localization-navigation-service';
import { LocalizationService } from '../../../../services/localization-service';

/**
 * Localization form component
 */
@Component({
  selector: 'localization-grid-form',
  templateUrl: './localization-grid-form.component.html',
  styleUrls: ['./localization-grid-form.component.css'],
})
export class LocalizationGridFormComponent extends FormBase<LocalizationModel> implements OnInit {
  /**
   * Update retrievers
   */
  protected updateRetrievers(): void {
    //Nothing
  }

  /**
   * Required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return [];
  }

  /**
   * Sets default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    //Nothing
  }

  /**
   * If a validation error occurs, this ethd is called.
   * Use it to inform the user, enable buttons, etc
   * @param data The data
   */
  public handleValidationError(data: LocalizationModel) {
    //Nothing (for now)
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param data The data that should be saved
   */
  public handlePreSave(data: LocalizationModel): Promise<boolean> {
    return this.noPreSave();
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param data The data that should be saved originally
   */
  public handleSaveAborted(data: LocalizationModel): void {
    //Nothing
  }

  /**
   * The oid to be loaded
   */
  @Input() oid: number;

  /**
 * Error message returned from upsert
 */
  public errorMessage: string;

  /**
   * Constructor
   * @param injector
   * @param localizationService
   * @param localizationNavigationService
   */
  constructor(protected injector: Injector,
    protected localizationService: LocalizationService,
    protected localizationNavigationService: LocalizationNavigationService) {
    super(injector);
  }

  /**
   * Loads the data. Please implement the 
   */
  protected loadData(): Promise<LocalizationModel> {
    if (this.oid > 0) {
      return new Promise<any>((resolve) => {
        this.localizationService.getLocalization(this.oid).pipe(take(1)).subscribe((data: LocalizationModel) => {
          resolve(data);
        });
      });
    } else {
      return new Promise<any>((resolve) => {
        const newModel = new LocalizationModel();
        newModel.isActive = true;//by default active, inactive when deleted
        resolve(newModel);
      });
    }
  }

  /**
   * Performs post-processing of the data
   * @param data Loaded data
   */
  protected postProcessData(data: LocalizationModel): Promise<LocalizationModel> {
    //filter locales => only locales that contain country code are supported by RD localization (f.e. en-US is ok, en not)
    this.reflistData.ref_l3_locale = this.reflistData.ref_l3_locale.filter(refItem => refItem.description?.indexOf("-") !== -1);
    return this.noProcessing(data);
  }

  /**
   * Creates the FormGroup and populates it with the data
   * @param data Loaded data
   */
  protected createFormGroup(data: LocalizationModel): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl({ value: data.id, disabled: true }),
      description: new UntypedFormControl(data.description),
      isDefault: new UntypedFormControl(data.isDefault),
      isActive: new UntypedFormControl(data.isActive),
      localeId: new UntypedFormControl(data.localeId),
      createdBy: new UntypedFormControl({ value: data.createdBy, disabled: true }),
      createdByDescription: new UntypedFormControl({ value: data.createdByDescription, disabled: true }),
      createdOn: new UntypedFormControl({ value: new Date(data.createdOn), disabled: true })
    });
  }

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return ['ref_l3_locale'];
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.init().then(() => {
      //Nothing
    });
  }

  /**
   * Parse any date values on load
   * @param data
   */
  protected parseDatesOnLoad(data: any) {
    //Nothing for now
  }

  /**
   * Format any dates for saving
   * @param data
   */
  protected formatDatesOnSave(data: any) {
    //Nothing for now
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.localizationService.closeForm();
    this.localizationNavigationService.displayAddEditIcons(true);
  }

  /**
   * Performs the actual saving
   * @param data The data to be saved
   */
  protected saveData(data: LocalizationModel): Promise<any> {
    if (this.validateFields(data)) {

      return new Promise<any>((resolve, reject) => {
        this.localizationService.updateLocalization(data).pipe(take(1)).subscribe({
          next: (data: any) => { resolve(data); },
          error: (error: any) => { reject(error); }
        });
      });
    }
    this.errorMessage = "Please enter the required information";

    return this.noSave();
  }

  /**
   * Validate form fields
   * @param data Data
   */
  validateFields(data: LocalizationModel): boolean {
    return data.description != "" && data.localeId !== null && data.localeId !== 0;
  }

  /**
   * Gets the data that should be saved
   * @param form The FormGroup
   * @param rawData The FormGroup's raw data
   */
  protected getSaveData(form: UntypedFormGroup, rawData: any): LocalizationModel {
    const data = new LocalizationModel(rawData);
    return data;
  }

  /**
   * Handles any post-save events
   * @param saveResult
   */
  protected handlePostSave(saveResult: any): void {
    if (saveResult.success) {
      this.errorMessage = null;
      this.close();
    } else {
      this.errorMessage = saveResult.message;
    }
  }

  /**
   * Handles any save errors
   * @param error
   */
  protected handleSaveError(error: any): void {
    alert(error);
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return true;
  }
}
